import { NextRouter } from "next/router";
import {
  Contributor,
  SendToAllVariant,
  getInvitationStatus,
} from "@memento/types/contributor";
import { MementoApiErrorCode } from "@memento/types/api-error";
import { GetAccessTokenFn } from "src/contexts/AuthProvider";
import constants from "src/constants";
import { sendInviteToContributors } from "src/requests/contributor";
import { formatDueDate, sendAnalyticsEvent } from "src/libs/analytics-lib";
import { hasErrorCode, MementoApiError } from "./api-error-lib";
import { getCardUrl } from "src/libs/cardUtils";
import { toastError } from "src/libs/toast-lib";
import urlJoin from "url-join";
import { TierLabel } from "@memento/types/subscription";

export function contributorHasUploadedSegment(
  contributor: Contributor
): boolean {
  return Boolean(contributor?.lastSegmentUploadedAt);
}

export function contributorHasVisitedPage(contributor: Contributor): boolean {
  return Boolean(contributor?.lastVisitedPageAt);
}

export function contributorHasBeenInvited(contributor: Contributor): boolean {
  return getInvitationStatus(contributor) === "sent";
}

export const SMS_REGEXP =
  /^(\+?\d{1,2}[\s.-]?)?(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})$/i;

/**
 * Normalize phone numbers by removing non-digits and adding
 * the country code if needed.   If the string does not appear
 * to be phone number, then the value is returned unaltered.
 *
 * @param sms phone number as string
 * @returns normalized phone number
 */
export const normalizeSms = (sms: string | undefined) => {
  let matches: RegExpMatchArray | null | undefined;

  if ((matches = sms?.match(SMS_REGEXP))) {
    const [, , numberPart] = matches;

    // this should never happen since we know the regex matches (unless the
    // regex changed, which could totally happy), but TS doesn't know that
    // (well, it does know that the regex could have changed and actually it is
    // possible that numPart is undefined, so, as infrequently as it happens to
    // be (actually, more frequently than we realize since TS is doing tons of
    // smart things all the time and we don't notice (or appreciate) it), TS is
    // pretty smart here)
    if (numberPart === undefined) {
      return sms;
    }

    const numbersOnly = numberPart.replace(/[^\d]/g, "");
    return `1${numbersOnly}`;
  } else {
    return sms;
  }
};

export const normalizeEmail = (email: string | undefined) => {
  return email ? email.toLowerCase() : undefined;
};

function compareContributorName(a: Contributor, b: Contributor): number {
  // put named contributors first
  if (!a.contributorName && b.contributorName) {
    return 1;
  } else if (a.contributorName && !b.contributorName) {
    return -1;
  } else {
    return a.contributorName.localeCompare(b.contributorName);
  }
}

function compareAddress(a: Contributor, b: Contributor): number {
  const addressA = a.email || a.sms;
  const addressB = b.email || b.sms;

  // should ever happen, but satisfies TS
  if (addressA === undefined || addressB === undefined) {
    return 0;
  }

  return addressA.localeCompare(addressB);
}

// sorting rules:
// 1. contributors with names will appear first
// 2. will sort by name
// 3. if names are equal, wlll sort by address
export function sortContributorsByName(
  contributors: Contributor[]
): Contributor[] {
  return [...contributors].sort((a, b) => {
    const comparison = compareContributorName(a, b);

    // if they're equal, sort by address
    return comparison === 0 ? compareAddress(a, b) : comparison;
  });
}

export function getUninvited(contributors: Contributor[]): Contributor[] {
  return contributors.filter(
    (x) =>
      getInvitationStatus(x) === "unsent" &&
      !Boolean(x.undeliverableAt) &&
      x.addedByOrganizer
  );
}

export function getInvited(contributors: Contributor[]): Contributor[] {
  return contributors.filter(
    (x) => getInvitationStatus(x) === "sent" && !Boolean(x.undeliverableAt)
  );
}

// Includes all contributors who have received or are pending an invite,
// regardless of deliverability. Use for analytics where the intent is "how
// many people were ever invited", not "how many active invites exist".
export function getEverInvited(contributors: Contributor[]): Contributor[] {
  return contributors.filter((x) => getInvitationStatus(x) !== "unsent");
}

export function getPending(contributors: Contributor[]): Contributor[] {
  return contributors.filter(
    (x) => getInvitationStatus(x) === "pending" && !Boolean(x.undeliverableAt)
  );
}

export function getBounced(contributors: Contributor[]): Contributor[] {
  return contributors.filter(
    (x) => !Boolean(x.lastInviteSentAt) && Boolean(x.undeliverableAt)
  );
}

// 1. Contributor added via organizer and invite was sent or pending
// 2. Contributor added via following general link and submitted video
export const getTracked = (contributors: Contributor[]) => {
  return contributors.filter((contributor) => {
    const { addedByOrganizer, lastVisitedPageAt, lastSegmentUploadedAt } =
      contributor;
    const status = getInvitationStatus(contributor);
    return (
      (addedByOrganizer && (status === "sent" || status === "pending")) ||
      (!addedByOrganizer && lastVisitedPageAt && lastSegmentUploadedAt)
    );
  });
};

export function getSubmitted(contributors: Contributor[]): Contributor[] {
  return contributors.filter(contributorHasUploadedSegment);
}

// Should only reliably be used after validation (since after validation we know
// for sure we either have an email or an SMS)
export function isEmail(x: string): boolean {
  return x.includes("@");
}

// Should only reliably be used after validation (since after validation we know
// for sure we either have an email or an SMS)
export function isSms(x: string): boolean {
  return !isEmail(x);
}

export function getFilteredContributors(
  contributors: Contributor[],
  searchFilter: string
): Contributor[] {
  if (searchFilter === "") {
    return contributors;
  }
  return contributors.filter((contributor) => {
    return (
      contributor.contributorName
        .toLowerCase()
        .includes(searchFilter.toLowerCase()) ||
      contributor?.email?.toLowerCase().includes(searchFilter.toLowerCase()) ||
      contributor?.sms?.includes(searchFilter.toLowerCase())
    );
  });
}

type HandleSendInvitationsProps = {
  cardDetails: {
    cardId: string;
    cardUrl: string;
    resendInvitesDisabledUntil?: number;
    dueDate?: number;
    occasionId?: string | null;
  };
  inviteEmailSubject: string;
  router: NextRouter;
  sendAllVariant: SendToAllVariant;
  getAccessToken: GetAccessTokenFn;
  isSending?: boolean;
  handleError?: boolean;
  username: string | undefined;
  tierLabel: TierLabel | undefined;
};

export const sendInvitations = async ({
  cardDetails: {
    cardId,
    cardUrl,
    resendInvitesDisabledUntil,
    dueDate,
    occasionId,
  },
  router,
  sendAllVariant,
  inviteEmailSubject,
  getAccessToken,
  username,
  tierLabel,
  handleError = false,
}: HandleSendInvitationsProps) => {
  try {
    const { numContributors, sendStatus } = await sendInviteToContributors(
      getAccessToken,
      cardId,
      { inviteEmailSubject, sendAllVariant }
    );

    const gvUrl = getCardUrl(cardUrl, cardId);
    const continuePath = gvUrl;
    const sendSuccessPath = `${gvUrl}/${constants.slugs.sendSuccessLink}`;

    if (sendAllVariant === SendToAllVariant.Unsent) {
      sendAnalyticsEvent({
        event: "InvitesSent",
        userId: username,
        tierLabel,
        data: {
          cardId,
          numberOfInvites: numContributors,
          cardDueDate: dueDate != null ? formatDueDate(dueDate) : undefined,
          cardOccasionId: occasionId ?? undefined,
        },
      });
    } else {
      sendAnalyticsEvent({
        event: "InvitesResent",
        userId: username,
        tierLabel,
        data: { cardId, numberOfInvites: numContributors },
      });
    }

    await router.push(
      {
        pathname: sendSuccessPath,
        query: {
          numSent: numContributors,
          continueUrl: continuePath,
          sendStatus,
        },
      },
      sendSuccessPath
    );

    return { numContributors };
  } catch (err) {
    const message =
      err instanceof MementoApiError &&
      hasErrorCode(MementoApiErrorCode.ContributorMultipleResendDisabled, err)
        ? `Multiple resend is disabled for this card until : ${resendInvitesDisabledUntil}`
        : err instanceof Error
          ? err.message
          : "Something went wrong and your invitation could not be sent.";

    // this error will be displayed on the `send` contributor page
    if (handleError) {
      toastError(message);
    }
    // this will be handled in resend invites
    else {
      throw err;
    }
  }
};

export const getTrackContributorsUrl = ({
  cardId,
  cardUrl,
}: {
  cardId: string;
  cardUrl: string;
}) => {
  const gvUrl = getCardUrl(cardUrl, cardId);

  return urlJoin(gvUrl, constants.slugs.trackContributorsLink);
};

export const getShareContributorsUrl = ({
  cardId,
  cardUrl,
}: {
  cardId: string;
  cardUrl: string;
}) => {
  const gvUrl = getCardUrl(cardUrl, cardId);

  return urlJoin(gvUrl, constants.slugs.socialShareLink);
};
